import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  Injector,
  ChangeDetectorRef,
  OnChanges,
  inject,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  NgControl,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { IconsModule } from '@progress/kendo-angular-icons';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { xIcon, clockIcon } from '@progress/kendo-svg-icons';
import { DATE_FORMATS } from '../../../../core/constants/date-formats';

export interface TimepickerConfig {
  label?: string;
  placeholder?: string;
  format?: string;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  showErrors?: boolean;
  hint?: string;
  size?: 'small' | 'medium' | 'large';
  fillMode?: 'solid' | 'outline' | 'flat';
  min?: Date | string | null;
  max?: Date | string | null;
  clearButton?: boolean;
  tabIndex?: number;
  value?: string | Date;
  hourStep?: number;
  minuteStep?: number;
  secondStep?: number;
  showSeconds?: boolean;
  use24Hour?: boolean;
  nowButton?: boolean;
  cancelButton?: boolean;
  meridiemPlaceholder?: string;
}

export interface TimeValidationConfig {
  required?: boolean;
  min?: Date | string;
  max?: Date | string;
  customValidators?: Array<(value: Date | null) => ValidationErrors | null>;
  errorMessages?: Record<string, string>;
  allowedTimes?: Array<{ hour: number; minute: number; second?: number }>;
  disallowedTimes?: Array<{ hour: number; minute: number; second?: number }>;
  businessHoursOnly?: boolean;
  businessHoursStart?: string;
  businessHoursEnd?: string;
}

@Component({
  selector: 'app-timepicker',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DateInputsModule,
    IconsModule,
    ButtonsModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimepickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TimepickerComponent),
      multi: true,
    },
  ],
  templateUrl: './timepicker.component.html',
  styleUrls: ['./timepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimepickerComponent implements ControlValueAccessor, Validator, OnInit, OnChanges {
  @Input() config: TimepickerConfig = {};
  @Input() validation: TimeValidationConfig = {};

  @Output() valueChange = new EventEmitter<Date | null>();
  @Output() onFocus = new EventEmitter<FocusEvent>();
  @Output() onBlur = new EventEmitter<FocusEvent>();
  @Output() onOpen = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();
  @Output() onClear = new EventEmitter<void>();

  clockIconSvg = clockIcon;
  xIconSvg = xIcon;

  value: Date | null = null;
  touched = false;
  errors: ValidationErrors | null = null;
  inputId = `timepicker-${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2, 11)}`;

  popupSettings: any = {};
  timeSteps: any = {};

  // Cached values to avoid recalculation on every change detection
  minTime: Date = new Date(0, 0, 0, 0, 0, 0);
  maxTime: Date = new Date(0, 0, 0, 23, 59, 59);

  private onTouchedCallback: () => void = () => {};
  private onChangeCallback: (_: any) => void = () => {};
  private readonly cdr = inject(ChangeDetectorRef);

  public ngControl: NgControl | null = null;

  constructor(private readonly injector: Injector) {}

  ngOnInit() {
    this.ngControl = this.injector.get(NgControl, null);
    this.initializeDefaults();
    this.setupTimeSteps();
    this.updateMinMaxTimes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      const prev = changes['config'].previousValue || {};
      const curr = changes['config'].currentValue || {};

      if (curr.value !== prev.value && this.config.value) {
        this.value = this.parseTimeValue(this.config.value);
      }

      // Only recreate timeSteps when step values actually change to avoid
      // Kendo re-rendering and closing the popup on every change detection cycle
      if (
        curr.hourStep !== prev.hourStep ||
        curr.minuteStep !== prev.minuteStep ||
        curr.secondStep !== prev.secondStep ||
        curr.showSeconds !== prev.showSeconds
      ) {
        this.setupTimeSteps();
      }

      // Only update min/max when they actually change
      if (curr.min !== prev.min || curr.max !== prev.max) {
        this.updateMinMaxTimes();
      }

      this.cdr.markForCheck();
    }

    if (changes['validation']) {
      this.updateMinMaxTimes();
      this.cdr.markForCheck();
    }
  }

  private updateMinMaxTimes(): void {
    this.minTime = this.config.min
      ? (this.parseTimeValue(this.config.min) ?? new Date(0, 0, 0, 0, 0, 0))
      : new Date(0, 0, 0, 0, 0, 0);

    this.maxTime = this.config.max
      ? (this.parseTimeValue(this.config.max) ?? new Date(0, 0, 0, 23, 59, 59))
      : new Date(0, 0, 0, 23, 59, 59);
  }

  private initializeDefaults() {
    this.config = {
      format: this.config.use24Hour === false ? DATE_FORMATS.TIME_12H : DATE_FORMATS.TIME,
      size: 'medium',
      fillMode: 'outline',
      showErrors: true,
      clearButton: false,
      disabled: false,
      readonly: false,
      required: false,
      hourStep: 1,
      minuteStep: 1,
      secondStep: 1,
      showSeconds: false,
      use24Hour: true,
      nowButton: false,
      cancelButton: true,
      ...this.config,
    };

    if (this.config.showSeconds && this.config.format === DATE_FORMATS.TIME) {
      this.config.format = DATE_FORMATS.TIME_WITH_SECONDS;
    }

    // Enhanced popup settings with smooth animation
    this.popupSettings = {
      popupClass: 'custom-time-popup smooth-scroll-popup',
      appendTo: 'root',
      animate: {
        type: 'slide',
        direction: 'down',
        duration: 200,
      },
    };

    this.validation = {
      required: this.config.required,
      errorMessages: {
        required: 'This field is required',
        min: this.validation?.min
          ? `Time must be on or after ${this.formatTime(this.parseTimeValue(this.validation?.min))}`
          : 'Time is too early',
        max: this.validation?.max
          ? `Time must be on or before ${this.formatTime(this.parseTimeValue(this.validation?.max))}`
          : 'Time is too late',
        invalidTime: 'Please enter a valid time',
        disallowedTime: 'This time is not available',
        businessHours: `Time must be within business hours`,
        ...this.validation?.errorMessages,
      },
      ...this.validation,
    };

    if (this.validation?.businessHoursOnly) {
      this.validation.businessHoursStart = this.validation?.businessHoursStart ?? '09:00';
      this.validation.businessHoursEnd = this.validation?.businessHoursEnd ?? '17:00';
    }
  }

  private setupTimeSteps() {
    this.timeSteps = {
      hour: this.config.hourStep || 1,
      minute: this.config.minuteStep || 1,
      second: this.config.showSeconds ? this.config.secondStep || 1 : 0,
    };
  }

  private parseTimeValue(value: string | Date | null | undefined): Date | null {
    if (!value) return null;

    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'string') {
      return this.parseTimeString(value);
    }

    return null;
  }

  private parseTimeString(value: string): Date | null {
    const timeRegex = /(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?/i;
    const timeParts = timeRegex.exec(value);
    if (!timeParts) return null;

    const hours = this.adjustHoursForMeridiem(Number.parseInt(timeParts[1], 10), timeParts[4]);
    const minutes = Number.parseInt(timeParts[2], 10);
    const seconds = timeParts[3] ? Number.parseInt(timeParts[3], 10) : 0;

    const today = new Date();
    today.setHours(hours, minutes, seconds, 0);
    return today;
  }

  private adjustHoursForMeridiem(hours: number, meridiem?: string): number {
    if (!meridiem) return hours;

    const isPM = meridiem.toUpperCase() === 'PM';
    const isAM = meridiem.toUpperCase() === 'AM';

    if (isPM && hours !== 12) return hours + 12;
    if (isAM && hours === 12) return 0;

    return hours;
  }

  private formatTime(date: Date | null): string {
    if (!date) return '';
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    if (this.config.showSeconds) {
      return `${hours}:${minutes}:${seconds}`;
    }
    return `${hours}:${minutes}`;
  }

  writeValue(value: any): void {
    if (value instanceof Date) {
      this.value = value;
    } else if (typeof value === 'string' && value) {
      this.value = this.parseTimeValue(value);
    } else {
      this.value = null;
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.config = { ...this.config, disabled: isDisabled };
    this.cdr.markForCheck();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const errors: ValidationErrors = {};

    if (!value) {
      if (this.config.required || this.validation?.required) {
        errors['required'] = true;
      }
      this.errors = Object.keys(errors).length ? errors : null;
      return this.errors;
    }

    const time = this.parseTimeValue(value);
    if (!time) {
      errors['invalidTime'] = true;
      this.errors = errors;
      return errors;
    }

    this.checkMinMax(time, errors);
    this.checkBusinessHours(time, errors);
    this.checkAllowedDisallowedTimes(time, errors);
    this.applyCustomValidators(time, errors);

    this.errors = Object.keys(errors).length ? errors : null;
    return this.errors;
  }

  private checkMinMax(time: Date, errors: ValidationErrors) {
    const min = this.validation.min ? this.parseTimeValue(this.validation.min) : null;
    const max = this.validation.max ? this.parseTimeValue(this.validation.max) : null;

    if (min && this.compareTime(time, min) < 0) {
      errors['min'] = true;
    }
    if (max && this.compareTime(time, max) > 0) {
      errors['max'] = true;
    }
  }

  private checkBusinessHours(time: Date, errors: ValidationErrors) {
    if (!this.validation.businessHoursOnly) return;

    const start = this.parseTimeValue(this.validation.businessHoursStart);
    const end = this.parseTimeValue(this.validation.businessHoursEnd);

    if (start && end) {
      if (this.compareTime(time, start) < 0 || this.compareTime(time, end) > 0) {
        errors['businessHours'] = true;
      }
    }
  }

  private checkAllowedDisallowedTimes(time: Date, errors: ValidationErrors) {
    const timeHours = time.getHours();
    const timeMinutes = time.getMinutes();
    const timeSeconds = time.getSeconds();

    if (this.validation.allowedTimes && this.validation.allowedTimes.length > 0) {
      const isAllowed = this.validation.allowedTimes.some(
        (allowed) =>
          allowed.hour === timeHours &&
          allowed.minute === timeMinutes &&
          (!this.config.showSeconds || allowed.second === timeSeconds)
      );
      if (!isAllowed) {
        errors['disallowedTime'] = true;
      }
    }

    if (this.validation.disallowedTimes && this.validation.disallowedTimes.length > 0) {
      const isDisallowed = this.validation.disallowedTimes.some(
        (disallowed) =>
          disallowed.hour === timeHours &&
          disallowed.minute === timeMinutes &&
          (!this.config.showSeconds || disallowed.second === timeSeconds)
      );
      if (isDisallowed) {
        errors['disallowedTime'] = true;
      }
    }
  }

  private applyCustomValidators(time: Date, errors: ValidationErrors) {
    if (!this.validation.customValidators) return;
    for (const validator of this.validation.customValidators) {
      const customErr = validator(time);
      if (customErr) Object.assign(errors, customErr);
    }
  }

  private compareTime(time1: Date, time2: Date): number {
    const t1 = time1.getHours() * 3600 + time1.getMinutes() * 60 + time1.getSeconds();
    const t2 = time2.getHours() * 3600 + time2.getMinutes() * 60 + time2.getSeconds();
    return t1 - t2;
  }

  get showErrorState(): boolean {
    if (!this.config.showErrors) return false;
    const hasErrors = !!(this.errors ?? this.ngControl?.control?.errors);
    if (!hasErrors) return false;

    const isTouched =
      this.touched || this.ngControl?.control?.touched || this.ngControl?.control?.dirty;
    return !!isTouched;
  }

  get currentErrorMessage(): string {
    if (!this.showErrorState) return '';
    const errors = this.ngControl?.control?.errors ?? this.errors;
    if (!errors) return '';
    const firstKey = Object.keys(errors)[0];
    return this.validation.errorMessages?.[firstKey] ?? 'Invalid time';
  }

  get hasValue(): boolean {
    return this.value !== null && this.value !== undefined;
  }

  get showClearButton(): boolean {
    return !!(this.config.clearButton && this.hasValue && !this.config.disabled);
  }

  handleValueChange(newValue: Date | null) {
    if (this.value?.getTime() === newValue?.getTime()) {
      return;
    }

    this.value = newValue;
    this.onChangeCallback(newValue);
    this.valueChange.emit(newValue);

    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }
    this.cdr.markForCheck();
  }

  handleBlur(event?: FocusEvent) {
    if (!this.touched) {
      this.touched = true;
      this.onTouchedCallback();
      this.cdr.markForCheck();
    }
    if (event) {
      this.onBlur.emit(event);
    }
  }

  handleFocus(event: FocusEvent) {
    this.onFocus.emit(event);
  }

  handleOpen() {
    this.onOpen.emit();
  }

  handleClose() {
    this.onClose.emit();
  }

  clearTime() {
    this.value = null;
    this.touched = true;
    this.onChangeCallback(this.value);
    this.onTouchedCallback();
    this.valueChange.emit(this.value);
    this.onClear.emit();

    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }
    this.cdr.markForCheck();
  }
}
